import { createContext, useCallback, useContext, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { Sheet } from "@/components/ui/sheet";
import { useIsMobileSidebar } from "@/components/layout/useSidebarViewport";
import { isAvailableHistoryTrigger } from "./history-sidebar-accessibility";

type PlaygroundPresentation = {
  modern: boolean;
  mobile: boolean;
  mobileHistoryOpen: boolean;
  setMobileHistoryOpen: (open: boolean) => void;
  mobileFocusAllowed?: RefObject<boolean>;
  settingsOpen: boolean;
  settingsId: string;
  settingsHost: HTMLDivElement | null;
  setSettingsHost: (host: HTMLDivElement | null) => void;
  settingsOpener: RefObject<HTMLButtonElement>;
  openSettings: (trigger: HTMLButtonElement) => void;
  closeSettings: (restoreFocus?: boolean) => void;
};

const Presentation = createContext<PlaygroundPresentation>({
  modern: false, mobile: false, mobileHistoryOpen: false, setMobileHistoryOpen: () => {},
  settingsOpen: false, settingsId: "", settingsHost: null, setSettingsHost: () => {},
  settingsOpener: { current: null }, openSettings: () => {}, closeSettings: () => {},
});
export const usePlaygroundPresentation = () => useContext(Presentation);

/** Presentation is local to the active AppShell route, never the standalone chat. */
export function PlaygroundPresentationProvider({ children, scopeKey = "" }: { children: ReactNode; scopeKey?: string }) {
  const mobile = useIsMobileSidebar();
  const viewport = useMemo(() => ({ mobile }), [mobile]);
  const scope = useMemo(() => ({ scopeKey }), [scopeKey]);
  const [settings, setSettings] = useState<{ viewport: object; scope: object } | null>(null);
  const settingsOpen = !mobile && settings?.viewport === viewport && settings.scope === scope;
  const [settingsHost, setSettingsHost] = useState<HTMLDivElement | null>(null);
  const settingsOpener = useRef<HTMLButtonElement | null>(null);
  const settingsId = useId();
  const openSettings = useCallback((trigger: HTMLButtonElement) => {
    settingsOpener.current = trigger;
    setSettings({ viewport, scope });
  }, [viewport, scope]);
  const closeSettings = useCallback((restoreFocus = false) => {
    setSettings(null);
    if (restoreFocus && isAvailableHistoryTrigger(settingsOpener.current)) settingsOpener.current.focus({ preventScroll: true });
  }, []);
  const [history, setHistory] = useState<{ viewport: object | null; open: boolean }>({ viewport: null, open: false });
  // Returning to mobile starts in chat, even if the old mobile overlay was open.
  const mobileHistoryOpen = mobile && history.viewport === viewport && history.open;
  const setMobileHistoryOpen = useCallback((open: boolean) => {
    setHistory({ viewport, open: mobile && open });
  }, [mobile, viewport]);
  const mobileFocusAllowed = useRef(false);
  useLayoutEffect(() => {
    mobileFocusAllowed.current = mobile && !mobileHistoryOpen;
    return () => { mobileFocusAllowed.current = false; };
  }, [mobile, mobileHistoryOpen]);
  const value = useMemo(() => ({ modern: true, mobile, mobileHistoryOpen, setMobileHistoryOpen, mobileFocusAllowed,
    settingsOpen, settingsId, settingsHost, setSettingsHost, settingsOpener, openSettings, closeSettings }),
    [mobile, mobileHistoryOpen, setMobileHistoryOpen, settingsOpen, settingsId, settingsHost, openSettings, closeSettings]);
  return <Presentation.Provider value={value}>
    <Sheet open={mobileHistoryOpen} onOpenChange={setMobileHistoryOpen}>{children}</Sheet>
  </Presentation.Provider>;
}
